import { STATUS_LABELS } from '@/components/judgments/StatusBadge'
import { cn } from '@/lib/cn'
import { Chip } from '@/components/ui/Chip'
import { Field, Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Toggle } from '@/components/ui/Toggle'
import type {
  JudgmentFacets,
  JudgmentFilters,
  JudgmentStatus,
  PartyRole,
} from '@/types/api'

export interface FilterPanelProps {
  filters: JudgmentFilters
  facets?: JudgmentFacets
  activeCount: number
  onChange: (update: Partial<JudgmentFilters>) => void
  onToggleArray: <K extends 'status' | 'filingLocation'>(
    key: K,
    value: JudgmentFilters[K][number],
  ) => void
  onReset: () => void
  className?: string
}

const ROLE_OPTIONS = [
  { value: 'debtor', label: 'Debtor' },
  { value: 'creditor', label: 'Creditor' },
]

/** The small uppercase label above each group of controls. */
function GroupLabel({ children }: { children: string }) {
  return (
    <legend className="mb-2 text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase">
      {children}
    </legend>
  )
}

/**
 * Every filter the API exposes, grouped by what it narrows.
 *
 * Facet counts come from the same filter set that produced the table, so a chip
 * showing "Open 154" is the count you get by clicking it.
 */
export function FilterPanel({
  filters,
  facets,
  activeCount,
  onChange,
  onToggleArray,
  onReset,
  className,
}: FilterPanelProps) {
  const statuses = facets?.statuses ?? []
  const locations = facets?.filingLocations ?? []

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-line pb-3">
        <h2 className="text-base font-semibold text-ink">Filters</h2>
        {activeCount > 0 ? (
          <button
            type="button"
            onClick={onReset}
            className="rounded-sm text-xs font-medium text-accent-text underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        ) : null}
      </div>

      <fieldset>
        <GroupLabel>Status</GroupLabel>
        <div className="flex flex-wrap gap-1.5">
          {statuses.length === 0 ? (
            <p className="text-xs text-ink-muted">No records loaded yet.</p>
          ) : (
            statuses.map((facet) => (
              <Chip
                key={facet.value}
                label={STATUS_LABELS[facet.value as JudgmentStatus] ?? facet.label}
                count={facet.count}
                selected={filters.status.includes(facet.value as JudgmentStatus)}
                onClick={() => onToggleArray('status', facet.value as JudgmentStatus)}
              />
            ))
          )}
        </div>
      </fieldset>

      {/* Every control in this sidebar is the compact size. The Role select
          was the default height while the date and amount fields below it were
          compact, which put two control heights in one 16rem column. */}
      <Field label="Role" htmlFor="filter-role">
        <Select
          id="filter-role"
          selectSize="sm"
          value={filters.partyRole ?? ''}
          placeholder="Either role"
          options={ROLE_OPTIONS}
          onChange={(event) =>
            onChange({ partyRole: (event.target.value || null) as PartyRole | null })
          }
        />
      </Field>

      <fieldset>
        <GroupLabel>County</GroupLabel>
        {/* Sized so the usual county list fits without scrolling at the current
            type scale; it still scrolls if a search spans more of them. */}
        <div className="scrollbar-slim fade-bottom flex max-h-64 flex-wrap gap-1.5 overflow-y-auto pb-6">
          {locations.length === 0 ? (
            <p className="text-xs text-ink-muted">No counties loaded yet.</p>
          ) : (
            locations.map((facet) => (
              <Chip
                key={facet.value}
                label={facet.label}
                count={facet.count}
                selected={filters.filingLocation.includes(facet.value)}
                onClick={() => onToggleArray('filingLocation', facet.value)}
              />
            ))
          )}
        </div>
      </fieldset>

      <fieldset>
        <GroupLabel>Date entered</GroupLabel>
        <div className="grid grid-cols-2 gap-2">
          <Field label="From" htmlFor="filter-date-from">
            <Input
              id="filter-date-from"
              type="date"
              inputSize="sm"
              value={filters.dateFrom}
              max={filters.dateTo || undefined}
              onChange={(event) => onChange({ dateFrom: event.target.value })}
            />
          </Field>
          <Field label="To" htmlFor="filter-date-to">
            <Input
              id="filter-date-to"
              type="date"
              inputSize="sm"
              value={filters.dateTo}
              min={filters.dateFrom || undefined}
              onChange={(event) => onChange({ dateTo: event.target.value })}
            />
          </Field>
        </div>
      </fieldset>

      <fieldset>
        <GroupLabel>Amount</GroupLabel>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Min $" htmlFor="filter-amount-min">
            <Input
              id="filter-amount-min"
              type="number"
              inputMode="decimal"
              inputSize="sm"
              min="0"
              placeholder="Any"
              value={filters.amountMin}
              onChange={(event) => onChange({ amountMin: event.target.value })}
            />
          </Field>
          <Field label="Max $" htmlFor="filter-amount-max">
            <Input
              id="filter-amount-max"
              type="number"
              inputMode="decimal"
              inputSize="sm"
              min="0"
              placeholder="Any"
              value={filters.amountMax}
              onChange={(event) => onChange({ amountMax: event.target.value })}
            />
          </Field>
        </div>
      </fieldset>

      <div className="border-t border-line pt-4">
        <Toggle
          id="filter-has-details"
          checked={filters.hasDetails === true}
          onChange={(checked) => onChange({ hasDetails: checked ? true : null })}
          label="Has docket &amp; amount"
          description="Only records whose detail page was read."
        />
      </div>
    </div>
  )
}
